// WorkloadDistribution/page.js
import React, { useMemo } from "react";


const WorkloadDistribution = ({ stories, users, sprint, departments = [], leaves = [] }) => {
  const nameOf = (u) =>
    u?.name || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || "Unknown";

  const workload = useMemo(() => {
    const map = {};

    // only Active employees (deboardEmp === false) with role Employee (3) or Team Lead (6)
    // should appear here — matches the same role filter used when assigning
    // Developer/Tester on a story (role.id === 3 || role.id === 6)
    // departments flagged as Scrum. If none are flagged, behave exactly as before.
    const scrumDeptIds = new Set(
      departments
        .filter((d) => d?.isScrum ?? d?.scrum)
        .map((d) => String(d.id))
    );

    const activeUsers = users.filter((u) => {
      const baseOk =
        u?.deboardEmp === false && (u?.role?.id === 3 || u?.role?.id === 6);
      if (!baseOk) return false;
      if (scrumDeptIds.size === 0) return true;
      const deptId = u?.department?.id ?? u?.departmentId;
      return deptId !== undefined && deptId !== null && scrumDeptIds.has(String(deptId));
    });
    const activeUserIds = new Set(activeUsers.map((u) => u?.id || u?._id));

    // seed every active employee first so people with 0 SP assigned still show up (available capacity)
    activeUsers.forEach((u) => {
      const uid = u?.id || u?._id;
      if (!uid) return;
      map[uid] = { uid, name: nameOf(u), assignedSP: 0, completedSP: 0 };
    });

    const credit = (user, sp, isCompleted) => {
      const uid = user?.id || user?._id;
      if (!uid || !sp) return; // no user in this role for this SP pool → ignored, not counted anywhere
      if (!activeUserIds.has(uid)) return; // inactive employee — never shown here, even if SP is assigned
      if (!map[uid]) map[uid] = { uid, name: nameOf(user), assignedSP: 0, completedSP: 0 };
      map[uid].assignedSP += sp;
      if (isCompleted) map[uid].completedSP += sp;
    };
    stories.forEach((st) => {
      const isCompleted = st.status === "COMPLETED";
      const devSP = Number(st.storyPoints) || 0;
      const testSP = Number(st.testStoryPoints) || 0;

      const devUser = st.storyUsers?.find((su) => su.userRole === "DEVELOPER")?.user;
      const testUser = st.storyUsers?.find((su) => su.userRole === "TESTER")?.user;

      // Dev SP only counts if a DEVELOPER is assigned; Test SP only if a TESTER is assigned.
      credit(devUser, devSP, isCompleted);
      credit(testUser, testSP, isCompleted);
    });

    // show every employee (including 0 SP = fully available), most-booked first
    return Object.values(map)
      .sort((a, b) => b.assignedSP - a.assignedSP);
  }, [stories, users, departments]);

  // total working-day capacity of the sprint — 1 SP assumed = 1 day of an employee's capacity
  const sprintCapacityDays = useMemo(() => {
    if (!sprint?.startDate || !sprint?.endDate) return 0;
    const [sy, sm, sd] = String(sprint.startDate).slice(0, 10).split("-").map(Number);
    const [ey, em, ed] = String(sprint.endDate).slice(0, 10).split("-").map(Number);
    const cur = new Date(Date.UTC(sy, sm - 1, sd));
    const end = new Date(Date.UTC(ey, em - 1, ed));
    let days = 0;
    while (cur <= end) {
      if (cur.getUTCDay() !== 0) days++;          // 0 = Sunday, so Sundays are skipped
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return days;
  }, [sprint]);
  // working days of approved leave that fall INSIDE the sprint, per employee (Sundays skipped, half day = 0.5)
  const leaveDaysByUser = useMemo(() => {
    const result = {};
    if (!sprint?.startDate || !sprint?.endDate) return result;
    const DAY = 86400000;
    const toUtc = (s) => {
      const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
      return Date.UTC(y, m - 1, d);
    };
    const sStart = toUtc(sprint.startDate);
    const sEnd = toUtc(sprint.endDate);

    leaves.forEach((l) => {
      if (l?.status !== "APPROVED") return;
      if (!l.fromDate || !l.toDate || l.employeeId == null) return;
      const from = Math.max(toUtc(l.fromDate), sStart);
      const to = Math.min(toUtc(l.toDate), sEnd);
      if (from > to) return; // leave is outside this sprint
      let days = 0;
      for (let t = from; t <= to; t += DAY) {
        if (new Date(t).getUTCDay() !== 0) days++; // skip Sundays
      }
      if (l.dayType === "HALF") days = days > 0 ? 0.5 : 0;
      result[l.employeeId] = (result[l.employeeId] || 0) + days;
    });
    return result;
  }, [leaves, sprint]);

  return (
    <div style={{
      background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
      boxShadow: "0 1px 4px rgba(0,0,0,.06)", padding: "16px 18px", height: "100%"
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 3 }}>
        Workload Distribution
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 12 }}>By assignee</div>

      {workload.length === 0 ? (
        <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>No stories assigned yet.</p>
      ) : (
        // After
        <div style={{ maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
          {workload.map((w) => {

            const leaveDays = leaveDaysByUser[w.uid] || 0;
            const baseDays = sprintCapacityDays || 1; // avoid divide-by-0 if sprint dates are missing
            const capacity = Math.max(baseDays - leaveDays, 0); // sprint days minus approved leave
            const onLeaveAll = capacity === 0;                  // on leave for the whole sprint
            const rawPct = onLeaveAll ? (w.assignedSP > 0 ? 100 : 0) : (w.assignedSP / capacity) * 100;
            const pct = Math.min(Math.round(rawPct), 100);
            const isOverbooked = onLeaveAll ? w.assignedSP > 0 : rawPct > 100;
            const barColor = isOverbooked ? "#ef4444" : "#10b981"; // red = overbooked, green = booked/has room

            // status badge: Available (room left) / Full (near/at capacity) / Overbooked (past capacity)
            const badge = isOverbooked
              ? { text: "Overbooked", bg: "#fee2e2", color: "#dc2626" }
              : rawPct >= 90
                ? { text: "Full", bg: "#fef3c7", color: "#d97706" }
                : { text: "Available", bg: "#dcfce7", color: "#16a34a" };

            const fmt = (n) => n.toFixed(1).replace(/\.0$/, ""); // matches your app's SP formatting elsewhere

            return (
              <div key={w.uid} style={{ marginBottom: 11 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>
                    {w.name}
                    {leaveDays > 0 && (
                      <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 400, marginLeft: 6 }}>
                        ({fmt(leaveDays)}d leave · {fmt(capacity)}d capacity)
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: barColor }}>
                    {onLeaveAll ? "On leave" : `${Math.round(rawPct)}%`}
                  </span>
                </div>
                <div style={{ height: 6, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    width: `${pct}%`,
                    minWidth: w.assignedSP > 0 ? 6 : 0,   // no sliver when nothing is assigned yet
                    height: "100%",
                    background: barColor,
                    borderRadius: 4,
                    transition: "width .4s ease",
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkloadDistribution;
